// src/lib/onboarding/first-step.ts
//
// THE first step for a student with no starting level yet. One answer, used by
// every surface that tells a new student what to do first.
//
// Review finding (2026-10-06): a student with no completed test was told three
// different things. The dashboard's main card said "Take a Checkpoint" while a
// "No baseline yet" notice under it sent them to the full SAT; the Study Plan
// led with general practice and said it had been "a while" since their last
// full-length (they had never taken one); the Tests page led with the full
// SAT. The /try teaser was a fourth answer for Free students.
//
// Decided: the Checkpoint is the first step for everyone (Free gets one a
// month, Pro and school students unlimited). It is about an hour and samples
// skills from every domain, which is what the plan and the recommendations need
// to stop being generic. The full-length SAT is the alternative, never a second
// primary.
//
// Two things outrank it, because they ARE the first step for that student: a
// test they already started (finish it), and a test their teacher assigned.
//
// Pure: client and server surfaces both use it.

import { formatMinutes, testLengthMinutes } from '@/lib/school/assignment-length'

export type FirstStepReason = 'checkpoint' | 'resume' | 'assigned'

export type FirstStep = {
  reason: FirstStepReason
  title: string
  /**
   * "About 1 hour, in four timed parts you can stop between" — always
   * stated, so the student knows what they're committing to. A phrase, not a
   * sentence: surfaces append ". {purpose}", ", adaptive." or " · ".
   */
  duration: string
  /** Why this is the first step, in one sentence. */
  purpose: string
  cta: string
  href: string
}

/** Minutes, break included, before any extended-time multiplier. */
const CHECKPOINT_MINUTES = testLengthMinutes('CHECKPOINT')
const FULL_SAT_MINUTES = testLengthMinutes('FULL_TEST')

/**
 * The hour is four timed parts, and a part's clock starts only when the
 * student begins it, so they can stop between parts and come back (the
 * Runner offers "Stop here" there). Students short on time read "about an
 * hour" as one sitting they don't have; saying it splits is what lets a
 * 20-minute window start it (time-boxed sessions, QA finding 8). Said once
 * here, so every surface that quotes the duration says it.
 *
 * "Timed parts", not "15-minute parts": with extended time a part is 22½ or
 * 30 minutes, and this line is shown before we know whose clock it is. The
 * time-box picker, which does know, names the real length.
 */
export const CHECKPOINT_DURATION = `About ${formatMinutes(CHECKPOINT_MINUTES)}, in four timed parts you can stop between`
export const FULL_SAT_DURATION = `About ${formatMinutes(FULL_SAT_MINUTES)}, including the break`

const CHECKPOINT_PURPOSE =
  'It samples skills from all 8 SAT domains to find your starting level, so your study plan and recommendations fit you.'
const FULL_SAT_PURPOSE =
  'A full-length practice SAT finds your starting level too, and gives you a 400–1600 score.'
/** The full SAT as the step itself (a teacher set it), not as the alternative. */
const ASSIGNED_FULL_SAT_PURPOSE = 'It finds your starting level and gives you a 400–1600 score.'

/** The first step itself. */
export const FIRST_STEP: FirstStep = {
  reason: 'checkpoint',
  title: 'Take a Checkpoint',
  duration: CHECKPOINT_DURATION,
  purpose: CHECKPOINT_PURPOSE,
  cta: 'Start Checkpoint',
  href: '/checkpoint',
}

/** The alternative, offered beside the first step and never instead of it. */
export const FIRST_STEP_ALTERNATIVE = {
  title: 'Prefer a full-length SAT?',
  duration: FULL_SAT_DURATION,
  purpose: FULL_SAT_PURPOSE,
  cta: 'Take a full-length SAT instead',
  href: '/sat',
} as const

const SECTION_NAME = { RW: 'Reading and Writing', MATH: 'Math' } as const

export type FirstStepInput = {
  /**
   * Whether the student has a starting level: a completed, non-voided
   * Checkpoint or a completed scored test (src/lib/onboarding/baseline.ts).
   */
  hasBaseline: boolean
  /** A test the student started and hasn't finished, which section if it is a
   *  one-section run (null = both), and the state of its clock. */
  inProgress?: {
    kind: 'CHECKPOINT' | 'SAT'
    sectionScope?: 'RW' | 'MATH' | null
    paused?: boolean
    timedOut?: boolean
    outOfTime?: boolean
  } | null
  /** An open test a teacher assigned. Its href carries the assignment context. */
  assigned?: { kind: 'CHECKPOINT' | 'FULL_TEST'; href: string } | null
}

/**
 * The open test a teacher set, earliest due first (the list arrives sorted by
 * due date). Practice assignments aren't a starting level, so they don't count.
 */
export function openAssignedTest(
  assignments: readonly { kind: string; href: string; done: boolean }[],
): FirstStepInput['assigned'] {
  const open = assignments.find(
    (a) => !a.done && (a.kind === 'CHECKPOINT' || a.kind === 'FULL_TEST'),
  )
  return open ? { kind: open.kind as 'CHECKPOINT' | 'FULL_TEST', href: open.href } : null
}

/**
 * The student's first step, or null once they have a starting level (then
 * nothing here applies and each surface goes back to its usual ranking).
 */
export function firstStep(input: FirstStepInput): FirstStep | null {
  if (input.hasBaseline) return null

  const open = input.inProgress
  if (open) {
    // A one-section run measures one section, so it must not promise what the
    // whole test gives: a one-section SAT has no 400–1600 total and isn't a
    // starting level (baseline.ts), and a one-section Checkpoint samples only
    // that section's domains.
    const section = open.sectionScope ? SECTION_NAME[open.sectionScope] : null
    // What opening it will do, the same states ResumeTestCard names: a module
    // that ran out of time isn't "picked up", it's submitted or decided on.
    const state = open.outOfTime
      ? 'Its time is up, so opening it hands it in'
      : open.timedOut
        ? 'Its time ran out. Open it to choose what to do'
        : open.paused
          ? 'Paused. Pick up where you left off'
          : 'Already started'
    if (open.kind === 'CHECKPOINT') {
      return {
        reason: 'resume',
        title: 'Finish your Checkpoint',
        duration: state,
        purpose: section
          ? `It samples skills from the 4 ${section} domains to find your ${section} starting level.`
          : CHECKPOINT_PURPOSE,
        cta: 'Resume Checkpoint',
        href: '/checkpoint',
      }
    }
    return {
      reason: 'resume',
      title: section ? `Finish your ${section} test` : 'Finish your practice SAT',
      duration: state,
      purpose: section
        ? `Finishing it gives you a 200–800 ${section} score. A Checkpoint after it finds your starting level.`
        : 'Finishing it gives you a 400–1600 score and finds your starting level.',
      cta: 'Resume test',
      href: '/sat',
    }
  }

  if (input.assigned) {
    const checkpoint = input.assigned.kind === 'CHECKPOINT'
    return {
      reason: 'assigned',
      title: checkpoint
        ? 'Take the Checkpoint your teacher assigned'
        : 'Take the practice SAT your teacher assigned',
      duration: checkpoint ? CHECKPOINT_DURATION : FULL_SAT_DURATION,
      purpose: checkpoint ? CHECKPOINT_PURPOSE : ASSIGNED_FULL_SAT_PURPOSE,
      cta: 'Start',
      href: input.assigned.href,
    }
  }

  return FIRST_STEP
}
